/**
 * Normalize page detail so UI can work with both content.sections (NEW) and content.blocks (OLD).
 * Rules:
 * - If page_json_v1.content.sections exists and has length → use as-is.
 * - Else if page_json_v1.content.blocks exists → convert blocks to sections.
 * - Never return empty content if either exists.
 */
import type { PageDetailOut } from '../types/catalog';
import type { ContentBlock } from '../types/page';

interface NormalizedSection {
  type: string;
  text?: string;
  items?: unknown[];
  title?: string;
  [key: string]: unknown;
}

function blocksToSections(blocks: ContentBlock[]): NormalizedSection[] {
  const sections: NormalizedSection[] = [];
  for (const b of blocks) {
    const type = (b.type ?? '').toLowerCase();
    const text = b.text?.trim();
    if (type === 'heading' && text) {
      sections.push({ type: 'heading', text });
    } else if (type === 'paragraph' && text) {
      sections.push({ type: 'paragraph', text });
    } else if (text) {
      sections.push({ type: 'paragraph', text });
    }
  }
  return sections;
}

/** Returns page with normalized page_json_v1.content.sections (creates from blocks if needed). */
export function normalizePageDetail(page: PageDetailOut | null): PageDetailOut | null {
  if (!page?.page_json_v1) return page;
  const content = page.page_json_v1.content as { sections?: NormalizedSection[]; blocks?: ContentBlock[] } | undefined;
  if (!content) return page;

  const hasSections = Array.isArray(content.sections) && content.sections.length > 0;
  const hasBlocks = Array.isArray(content.blocks) && content.blocks.length > 0;

  if (hasSections) {
    return page;
  }
  if (hasBlocks) {
    const sections = blocksToSections(content.blocks!);
    return {
      ...page,
      page_json_v1: {
        ...page.page_json_v1,
        content: { ...content, sections },
      },
    };
  }
  return page;
}

/** True only when sections, blocks, and cards are all absent or empty. */
export function isContentEmpty(page: PageDetailOut | null): boolean {
  if (!page?.page_json_v1?.content) return true;
  const c = page.page_json_v1.content as {
    sections?: unknown[];
    blocks?: unknown[];
    cards?: unknown[];
  };
  const sectionsLen = Array.isArray(c.sections) ? c.sections.length : 0;
  const blocksLen = Array.isArray(c.blocks) ? c.blocks.length : 0;
  const cardsLen = Array.isArray(c.cards) ? c.cards.length : 0;
  return sectionsLen === 0 && blocksLen === 0 && cardsLen === 0;
}
